// Tipos para Linhas de Cuidado

export enum CareLineStatus {
  ACTIVE = 'ACTIVE',
  PAUSED = 'PAUSED',
  COMPLETED = 'COMPLETED',
  DISCHARGED = 'DISCHARGED'
}

export interface ProtocolPhase {
  code: string;
  name: string;
  order: number;
  durationDays?: number;
  questionnaires?: string[];
  contents?: string[];
}

export interface AlertRule {
  condition: string;
  message: string;
  severity: string;
}

export interface CareLineProtocol {
  questionnaireIds?: string[];
  educationalContentIds?: string[];
  alertRules?: Record<string, AlertRule>;
  phases?: ProtocolPhase[];
}

export interface CareLineResponse {
  id: string;
  code: string;
  name: string;
  description?: string;
  protocol?: CareLineProtocol;
  monitoringFrequencyDays?: number;
  active: boolean;
  activePatientCount: number;
  createdAt: string;
  updatedAt?: string;
}

export interface CareLineListResponse {
  id: string;
  code: string;
  name: string;
  description?: string;
  monitoringFrequencyDays?: number;
  active: boolean;
  activePatientCount: number;
}

export interface PatientCareLineResponse {
  id: string;
  patientId: string;
  patientName: string;
  careLineId: string;
  careLineCode: string;
  careLineName: string;
  enrolledAt: string;
  enrolledByName?: string;
  status: CareLineStatus;
  currentPhase?: string;
  currentPhaseName?: string;  // Nome traduzido da fase (ex: "Avaliação Inicial")
  notes?: string;
  daysEnrolled: number;
  dischargedAt?: string;
  dischargedByName?: string;
  dischargeReason?: string;
  createdAt: string;
  updatedAt?: string;
}

export interface EnrollPatientRequest {
  patientId: string;
  initialPhase?: string;
  notes?: string;
}

export interface UpdatePatientCareLineRequest {
  status?: CareLineStatus;
  currentPhase?: string;
  notes?: string;
}

export interface DischargePatientRequest {
  reason: string;
  notes?: string;
}

export interface CareLineStatistics {
  totalActivePatients: number;
  byCareLineCount: CareLineCount[];
}

export interface CareLineCount {
  id: string;
  code: string;
  name: string;
  count: number;
}

// Labels para exibição
export const CareLineStatusLabels: Record<CareLineStatus, string> = {
  [CareLineStatus.ACTIVE]: 'Ativo',
  [CareLineStatus.PAUSED]: 'Pausado',
  [CareLineStatus.COMPLETED]: 'Concluído',
  [CareLineStatus.DISCHARGED]: 'Alta'
};

export const CareLineStatusColors: Record<CareLineStatus, {
  color: string;
  bgColor: string;
}> = {
  [CareLineStatus.ACTIVE]: {
    color: '#16a34a',
    bgColor: '#dcfce7'
  },
  [CareLineStatus.PAUSED]: {
    color: '#ca8a04',
    bgColor: '#fef9c3'
  },
  [CareLineStatus.COMPLETED]: {
    color: '#2563eb',
    bgColor: '#dbeafe'
  },
  [CareLineStatus.DISCHARGED]: {
    color: '#6b7280',
    bgColor: '#f3f4f6'
  }
};

// Códigos de linhas de cuidado padrão
export const CareLineCodes = {
  MENTAL_HEALTH: 'MENTAL_HEALTH',
  CHRONIC_METABOLIC: 'CHRONIC_METABOLIC',
  OBESITY: 'OBESITY',
  FRAIL_ELDERLY: 'FRAIL_ELDERLY',
  PRENATAL: 'PRENATAL',
  SMOKING_CESSATION: 'SMOKING_CESSATION'
} as const;

export const CareLineNames: Record<string, string> = {
  [CareLineCodes.MENTAL_HEALTH]: 'Saúde Emocional',
  [CareLineCodes.CHRONIC_METABOLIC]: 'Crônicos Metabólicos',
  [CareLineCodes.OBESITY]: 'Obesidade',
  [CareLineCodes.FRAIL_ELDERLY]: 'Idoso Frágil',
  [CareLineCodes.PRENATAL]: 'Gestante',
  [CareLineCodes.SMOKING_CESSATION]: 'Tabagismo'
};
